import React, { useState, useEffect } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import toast from "react-hot-toast";
import Spinner from "../Loading/Spinner";
import { useSelector } from "react-redux";
import { ApiURL } from "../../setting/GlobleVariable";

// Local Stripe instance for this payment flow
let stripePromise = null;
// Load Stripe.js on first use only (the /pure entry does not inject it on import).
// The server's key (GET /api/payment/config) wins, so switching Stripe from
// test to live is a server setting only; the build-time key is the fallback.
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = fetch(`${ApiURL}/payment/config`)
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}))
      .then((cfg) => {
        const key =
          typeof cfg?.publishableKey === "string" && cfg.publishableKey.startsWith("pk_")
            ? cfg.publishableKey
            : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
        return loadStripe(key);
      });
  }
  return stripePromise;
};

const StripePaymentForm = ({
  show,
  onPaymentCompleted,
  totalamount,
  setShow,
  setError,
  clientSecret,
  scrollMode = "inner",
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const isloadingcreateOrder = useSelector((state) => state.events.isLoading);
  const isloadingUserOrder = useSelector((state) => state.order.isloading);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError("");
    setMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success`,
        },
        redirect: "if_required",
      });

      if (error) {
        setMessage(error.message);
        setError(error.message);
        toast.error(error.message || "Payment failed");
        setIsProcessing(false);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        setMessage("Payment succeeded!");
        toast.success("Payment successful!");
        onPaymentCompleted({
          payment_intent_id: paymentIntent.id,
          paymentType: "credit_card",
          paymentMethod: "StripeCard",
          status: paymentIntent.status,
        });
        setIsProcessing(false);
      } else {
        setMessage(`Unexpected status: ${paymentIntent?.status}`);
        setIsProcessing(false);
      }
    } catch (err) {
      console.error("Payment error:", err);
      setError(err.message || "An error occurred during payment");
      toast.error(err.message || "An error occurred during payment");
      setIsProcessing(false);
    }
  };

  if (!show) {
    return null;
  }

  const paymentFields =
    scrollMode === "parent" ? (
      <PaymentElement />
    ) : (
      <div className="max-h-[min(52vh,440px)] overflow-y-auto overscroll-y-contain pr-1 [-webkit-overflow-scrolling:touch]">
        <PaymentElement />
      </div>
    );

  return (
    <form onSubmit={handleSubmit} className="stripe-payment-form flex flex-col">
      {paymentFields}
      <button
        type="submit"
        className="mt-4 w-full bg-[black] px-6 py-2.5 text-[white] cursor-pointer"
        disabled={isProcessing || !stripe || !elements || isloadingcreateOrder || isloadingUserOrder}
      >
        {isProcessing ? (
          <div className="flex items-center justify-center">
            <Spinner />
            <span className="ml-2">Processing...</span>
          </div>
        ) : (
          `Pay $${totalamount?.toFixed(2) || "0.00"}`
        )}
      </button>
      {message && (
        <div className="mt-4 text-sm text-red-600">{message}</div>
      )}
    </form>
  );
};

const StripePayment = ({
  show,
  onPaymentCompleted,
  totalamount,
  setShow,
  createTevoClient,
  setError,
  scrollMode = "inner",
}) => {
  const [clientSecret, setClientSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.user.user);
  const singleCouponCode = useSelector(
    (state) => state.couponcode.singleCouponCode
  );

  // Calculate final amount with coupon
  const calculateFinalAmount = () => {
    let finalAmount = totalamount || 0;
    if (singleCouponCode?.value) {
      if (singleCouponCode?.percentage) {
        finalAmount = totalamount - (singleCouponCode.value * totalamount) / 100;
      } else {
        finalAmount = Math.max(totalamount - singleCouponCode.value, 0);
      }
    }
    return finalAmount;
  };

  useEffect(() => {
    const createPaymentIntent = async () => {
      if (show === true) {
        try {
          setLoading(true);
          setError("");

          const finalAmount = calculateFinalAmount();

          if (finalAmount <= 0) {
            setError("Invalid amount");
            toast.error("Invalid payment amount");
            setLoading(false);
            return;
          }

          const response = await fetch(`${ApiURL}/payment/create-payment-intent`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              token: localStorage.getItem("instapass_user_token") || "",
            },
            body: JSON.stringify({
              amount: finalAmount,
              currency: "usd",
              metadata: {
                userId: user?._id || "guest",
                email: user?.email || "",
              },
            }),
          });

          const data = await response.json();

          if (response.status === 400 || response.status === 500) {
            setShow(0);
            setError(data?.message || "Something went wrong");
            toast.error(data?.message || "Something went wrong");
            setLoading(false);
            return;
          }

          if (data.success && data.clientSecret) {
            setClientSecret(data.clientSecret);
          } else {
            setError("Failed to create payment intent");
            toast.error("Failed to create payment intent");
          }
          setLoading(false);
        } catch (error) {
          console.error("Error creating payment intent:", error);
          setError(error.message || "Something went wrong");
          toast.error(error.message || "Something went wrong");
          setLoading(false);
        }
      } else {
        setClientSecret("");
      }
    };

    if (show === true) {
      createPaymentIntent();
    }
  }, [show, totalamount, singleCouponCode]);

  if (!show) {
    return null;
  }

  if (loading) {
    return (
      <div className="w-full flex justify-center place-items-center py-8">
        <Spinner />
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Failed to initialize payment. Please try again.</p>
      </div>
    );
  }

  return (
    <div style={{ display: `${show ? "block" : "none"}` }}>
      <Elements stripe={getStripe()} options={{ clientSecret }}>
        <StripePaymentForm
          show={show}
          onPaymentCompleted={onPaymentCompleted}
          totalamount={calculateFinalAmount()}
          setShow={setShow}
          setError={setError}
          clientSecret={clientSecret}
          scrollMode={scrollMode}
        />
      </Elements>
    </div>
  );
};

export default StripePayment;
